import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInventoryItemById } from '../services/inventoryService';
import { InventoryItem } from '../utils/types';
import { currencyFormat } from '../utils/constants';
import { ArrowBack, Facebook as FacebookIcon } from '@mui/icons-material';
import { postProductToFacebook } from '../services/facebookService';

export const InventoryDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [postMessage, setPostMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        setError('معرف العنصر غير متوفر');
        setLoading(false);
        return;
      }
      
      try {
        const fetchedItem = await getInventoryItemById(id);
        setItem(fetchedItem);
        setLoading(false);
      } catch (err) {
        console.error('Error loading inventory item:', err);
        setError('حدث خطأ أثناء تحميل بيانات العنصر');
        setLoading(false);
      }
    };
    
    loadItem();
  }, [id]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleFacebookPost = async () => {
    if (!item) return;
    setIsPosting(true);
    setPostMessage(null);
    try {
      const result = await postProductToFacebook(item);
      if (result.success) {
        setPostMessage({ type: 'success', text: 'تم النشر على فيسبوك بنجاح!' });
      } else {
        setPostMessage({ type: 'error', text: result.message });
      }
    } catch (err) {
      setPostMessage({ type: 'error', text: 'حدث خطأ أثناء الاتصال بفيسبوك' });
    } finally {
      setIsPosting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">لم يتم العثور على العنصر</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={handleGoBack} 
        >
          العودة
        </Button>

        <Button
          variant="contained"
          color="primary"
          startIcon={isPosting ? <CircularProgress size={20} color="inherit" /> : <FacebookIcon />}
          onClick={handleFacebookPost}
          disabled={isPosting}
          sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166fe5' } }}
        >
          {isPosting ? 'جاري النشر...' : 'نشر على فيسبوك'}
        </Button>
      </Box>

      {postMessage && (
        <Alert severity={postMessage.type} sx={{ mb: 2 }} onClose={() => setPostMessage(null)}>
          {postMessage.text}
        </Alert>
      )}
      
      <Typography variant="h4" gutterBottom>
        {item.name}
      </Typography>
      
      <Grid container spacing={3}>
        {/* بطاقة معلومات العنصر الأساسية */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            {item.imageUrl && (
              <CardMedia
                component="img"
                height="200"
                image={item.imageUrl}
                alt={item.name}
                sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
              />
            )}
            <CardContent>
              <Typography variant="h6" gutterBottom>
                المعلومات الأساسية
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>الرمز:</strong> {item.sku}
                </Typography>
                <Typography variant="body1">
                  <strong>الفئة:</strong> {item.category}
                </Typography>
                {item.subcategory && (
                  <Typography variant="body1">
                    <strong>الفئة الفرعية:</strong> {item.subcategory}
                  </Typography>
                )}
                <Typography variant="body1">
                  <strong>المورّد:</strong> {item.supplier || 'غير محدد'}
                </Typography>
                {item.description && (
                  <Typography variant="body1">
                    <strong>الوصف:</strong> {item.description}
                  </Typography>
                )}
                <Typography variant="body1">
                  <strong>متوفر في المتجر:</strong> {item.showInStore ? 'نعم' : 'لا'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* معلومات الكميات */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              معلومات الكميات
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">
                <strong>الكمية الإجمالية:</strong> {item.quantity || 0}
              </Typography>
              <Typography variant="body1">
                <strong>الحد الأدنى للكمية:</strong> {item.minQuantity || 0}
              </Typography>
              <Typography variant="body1" color={item.quantity <= (item.minQuantity || 0) ? 'error' : 'inherit'}>
                <strong>حالة المخزون:</strong> {item.quantity <= (item.minQuantity || 0) ? 'منخفض' : 'طبيعي'}
              </Typography>
            </Box>
            
            {/* عرض الكميات في المخازن المختلفة */}
            {item.warehouseQuantities && item.warehouseQuantities.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  الكميات حسب المخزن
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>المخزن</TableCell>
                        <TableCell align="right">الكمية</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.warehouseQuantities.map((wq, index) => (
                        <TableRow key={index}>
                          <TableCell>{wq.warehouseName}</TableCell>
                          <TableCell align="right">{wq.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* معلومات الأسعار */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              معلومات الأسعار
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2, display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="body1">
                  <strong>سعر الشراء:</strong> {currencyFormat(item.purchasePrice || 0)}
                </Typography>
                <Typography variant="body1">
                  <strong>سعر البيع:</strong> {currencyFormat(item.sellingPrice || 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1">
                  <strong>هامش الربح:</strong> {currencyFormat((item.sellingPrice || 0) - (item.purchasePrice || 0))}
                </Typography>
                <Typography variant="body1">
                  <strong>نسبة الربح:</strong> {
                    item.purchasePrice > 0 
                      ? `${(((item.sellingPrice || 0) - (item.purchasePrice || 0)) / (item.purchasePrice || 1) * 100).toFixed(2)}%` 
                      : 'غير متاح'
                  }
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default InventoryDetailsPage;